import React from "react";
import styled, { css } from "styled-components";

import { regular, medium, bold } from "core/fontStyles";
import breakpoints from "core/breakpoints";
import colors from "core/colors";

import CheckMarkApprove from "core/jsSvgIcons/CheckMarkApprove";

export const inputFocusStyle = ({ inverse }) => css`
    border: 3px solid ${inverse ? colors.inverseFocus : colors.mist};
    outline: none;
    box-shadow: 0 0 0 3px ${colors.focus};
`;

export const mediumHoverStyle = css`
    padding: calc(0.5rem + 1px);
`;

export const labelStyles = css`
    color: ${colors.storm};
    padding-top: 0.3125rem; /* 5px */
    display: inline-block;
    ${medium}
`;

export const labelAndErrorWrapperSharedStyle = css`
    font-size: 0.75rem; /* 12px */
    min-height: 1.5rem; /* 24px */
`;

// input
const inputStylesBySize = {
    small: css`
        border-radius: 0;
        -webkit-appearance: none; // remove inner shadow on iOS devices
        font-size: 0.75rem;
        line-height: 0.75rem;
        padding: 0.3125rem 0.4375rem;

        &:hover {
            padding: calc(0.3125rem + 1px) calc(0.4375rem + 1px);
        }

        &:hover {
            &:focus,
            &:read-only {
                padding: 0.3125rem 0.4375rem;
            }
        }

        &:disabled {
            &,
            &:hover {
                padding: calc(0.3125rem + 2px) calc(0.4375rem + 2px);
            }
        }
    `,
    medium: css`
        border-radius: 0;
        -webkit-appearance: none; // remove inner shadow on iOS devices
        font-size: 1rem;
        line-height: 1rem;
        padding: 0.5rem;

        &:hover {
            ${mediumHoverStyle}

            &:focus,
            &:read-only {
                padding: 0.5rem;
            }
        }

        &:disabled {
            &,
            &:hover {
                padding: calc(0.5rem + 2px);
            }
        }
    `,
    large: css`
        border-radius: 0;
        -webkit-appearance: none; // remove inner shadow on iOS devices
        font-size: 1.125rem;
        line-height: 1.125rem;
        padding: 0.625rem;

        &:hover {
            padding: calc(0.625rem + 1px);
        }

        &:hover {
            &:focus,
            &:read-only {
                padding: 0.625rem;
            }
        }

        &:disabled {
            &,
            &:hover {
                padding: calc(0.625rem + 2px);
            }
        }
    `
};

export const Input = styled.input`
    ${({ inverse, size }) => css`
        ${size ? inputStylesBySize[size] : inputStylesBySize.medium}
        color: ${inverse ? colors.snow : colors.midnight};
        border: 3px solid ${
            inverse ? colors.inverseFocus : colors.veryLightPink
        };
        ${regular}
        width: 100%;
        background-color: ${inverse ? "transparent" : colors.snow};

        @media (min-width: ${breakpoints.sm}rem) {
            ${size && inputStylesBySize[size.sm]}
        }

        @media (min-width: ${breakpoints.md}rem) {
            ${size && inputStylesBySize[size.md]}
        }

        @media (min-width: ${breakpoints.lg}rem) {
            ${size && inputStylesBySize[size.lg]}
        }

        &::placeholder {
            color: ${inverse ? colors.platinum : colors.storm};
        }

        &:hover {
            border: 2px solid
                ${inverse ? colors.platinum : colors.storm};
        }

        &:focus {
             ${inputFocusStyle({ inverse })};
        }

        &:disabled {
            color: ${inverse ? colors.platinum : colors.storm};
            background-color: ${
                inverse ? colors.charcoalGreyTwo : colors.veryLightPink
            };
            border: 1px solid
                ${inverse ? colors.charcoalGrey : colors.concrete};
        }

        &:read-only {
            border: 3px solid ${inverse ? "transparent" : colors.snow};
            color: ${inverse ? colors.snow : colors.black};
        }
    `}
`;

export const TextArea = styled.textarea`
    ${({ inverse, size }) => css`
        ${inputStylesBySize.medium}
        color: ${inverse ? colors.snow : colors.black};
        border: 3px solid ${
            inverse ? colors.inverseFocus : colors.veryLightPink
        };
        font-family: inherit;
        width: 100%;
        background-color: ${inverse ? "transparent" : colors.snow};

        @media (min-width: ${breakpoints.sm}rem) {
            ${size && inputStylesBySize[size.sm]}
        }

        @media (min-width: ${breakpoints.md}rem) {
            ${size && inputStylesBySize[size.md]}
        }

        @media (min-width: ${breakpoints.lg}rem) {
            ${size && inputStylesBySize[size.lg]}
        }

        &::placeholder {
            color: ${inverse ? colors.platinum : colors.storm};
        }

        &:hover {
            border: 2px solid
                ${inverse ? colors.platinum : colors.storm};
        }

        &:focus { ${inputFocusStyle({ inverse })};
        }

        &:disabled {
            color: ${inverse ? colors.platinum : colors.storm};
            background-color: ${
                inverse ? colors.charcoalGreyTwo : colors.veryLightPink
            };
            border: 1px solid
                ${inverse ? colors.charcoalGrey : colors.concrete};
        }

        &:read-only {
            border: 3px solid ${inverse ? "transparent" : colors.snow};
            color: ${inverse ? colors.snow : colors.black};
        }
    `}
`;

export const InputWrapper = styled.div`
    position: relative;
    width: 100%;
`;

// label
export const Label = styled.label`
    ${labelStyles}
`;

// reusable
export const AccentBorder = styled.div`
    position: absolute;
    bottom: 0;
    width: 100%;
    height: 3px;
`;

// success state
export const SuccessBorder = styled(AccentBorder)`
    background-color: ${colors.aquaGreenTwo};
`;

export const SuccessIconWrapper = styled.div`
    position: absolute;
    right: 0.8875rem;
    top: 50%;
    margin-top: -0.625rem;
`;

export const StyledCheckMarkApprove = styled(CheckMarkApprove)`
    width: 1rem;
    fill: ${({ inverse }) => (inverse ? "#86FFC5" : colors.jungleGreen)};
`;

let SuccessIcon = ({ inputSuccessful, inverse, className }) => (
    <SuccessIconWrapper inputSuccessful={inputSuccessful} className={className}>
        <StyledCheckMarkApprove inverse={inverse} />
    </SuccessIconWrapper>
);

SuccessIcon = styled(SuccessIcon)``;
export { SuccessIcon };

// error state
export const ErrorBorder = styled(AccentBorder)`
    background-color: ${({ inverse }) =>
        inverse ? colors.rose : colors.brick};
`;

export const ErrorMessage = styled.div`
    padding-top: 0.3125rem; /* 5px */
    display: inline-block;
    ${bold}
    color: ${({ inverse }) => (inverse ? colors.rose : colors.brick)};
`;

// wrappers
export const LabelAndErrorWrapper = styled.div`
    position: absolute;
    ${labelAndErrorWrapperSharedStyle}
`;

export const StyledFormInput = styled.div`
    ${regular}
    position: relative;

    /* Mobile-only, up to 767px: */
    @media (max-width: 47.94rem) {
        /* using breakpoints here isnt working for some reason */
        width: 100%;
    }
`;

// misc
export const PlaceholderContent = styled.div``;
